import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Layout } from '@/components/layout';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useGetMe } from '@workspace/api-client-react';
import { useToast } from '@/hooks/use-toast';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import {
  Truck, Plus, Trash2, RefreshCw, ToggleLeft, ToggleRight,
  CheckCircle2, XCircle, AlertTriangle, Loader2, ChevronDown,
  ChevronUp, Search, Package, Zap, ExternalLink, Pencil,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');
type SupplierPlatform = 'shopify' | 'bigcommerce' | 'wordpress' | 'neto' | 'neto_storefront';

function platformLabel(platform: SupplierPlatform | string): string {
  if (platform === 'bigcommerce') return 'BigCommerce';
  if (platform === 'wordpress') return 'WooCommerce';
  if (platform === 'neto') return 'Neto / Maropost API';
  if (platform === 'neto_storefront') return 'Neto public storefront';
  return 'Shopify';
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface ShopifyStore {
  id: string;
  nickname: string;
  storeUrl: string;
}

interface CacheInfo {
  cached: boolean;
  builtAt: string | null;
  skuCount: number;
}

interface SupplierConnection {
  id: string;
  storeId: string;
  supplierName: string;
  supplierShopifyUrl: string;
  platform: string;
  vendorName: string | null;
  supplierAdminTokenConfigured?: boolean;
  netoApiUsernameMasked?: string | null;
  netoApiKeyConfigured?: boolean;
  carrierServiceId: string | null;
  fallbackRateCents: number;
  fallbackRateName: string;
  enabled: boolean;
  inventoryEnabled: boolean;
  markOosWhenMissing: boolean;
  createdAt: string;
  updatedAt: string;
  cacheInfo: CacheInfo;
}

interface TestRate {
  service_name: string;
  total_price: number;
  currency: string;
  description?: string;
}

interface VendorProduct {
  id: string;
  title: string;
  variants: { id: string; title: string; sku: string }[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h >= 24) return `${Math.floor(h / 24)}d ago`;
  if (h > 0) return `${h}h ago`;
  if (m > 0) return `${m}m ago`;
  return 'just now';
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function ShippingRates() {
  const token = useAuthStore((s) => s.token);
  const authOpts = useAuthHeaders();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const { data: me, isLoading: meLoading } = useGetMe({
    request: authOpts,
    query: { enabled: !!token, retry: false },
  });

  useEffect(() => {
    if (!meLoading && me && !(me as any).featureFlags?.supplier_sync) {
      setLocation('/');
    }
  }, [meLoading, me]);

  const [stores, setStores] = useState<ShopifyStore[]>([]);
  const [connections, setConnections] = useState<SupplierConnection[]>([]);
  /** carrierServiceId per storeId — one carrier service per store, not per supplier */
  const [carrierServiceByStore, setCarrierServiceByStore] = useState<Record<string, { carrierServiceId: string | null }>>({});
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState<Record<string, boolean>>({});

  // Add-supplier form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStoreId, setNewStoreId] = useState('');
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierUrl, setNewSupplierUrl] = useState('');
  const [newPlatform, setNewPlatform] = useState<SupplierPlatform>('shopify');
  const [newVendorName, setNewVendorName] = useState('');
  const [newNetoApiUsername, setNewNetoApiUsername] = useState('');
  const [newNetoApiKey, setNewNetoApiKey] = useState('');
  const [newFallbackCents, setNewFallbackCents] = useState(1500);
  const [newFallbackName, setNewFallbackName] = useState('Standard Shipping');
  const [addBusy, setAddBusy] = useState(false);

  // Edit supplier state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editPlatform, setEditPlatform] = useState<SupplierPlatform>('shopify');
  const [editVendorName, setEditVendorName] = useState('');
  const [editAdminToken, setEditAdminToken] = useState('');
  const [editNetoApiUsername, setEditNetoApiUsername] = useState('');
  const [editNetoApiKey, setEditNetoApiKey] = useState('');
  const [editFallbackCents, setEditFallbackCents] = useState(1500);
  const [editFallbackName, setEditFallbackName] = useState('Standard Shipping');
  const [editBusy, setEditBusy] = useState(false);

  function openEdit(conn: SupplierConnection) {
    setEditingId(conn.id);
    setEditName(conn.supplierName);
    setEditUrl(conn.supplierShopifyUrl);
    setEditPlatform((conn.platform as SupplierPlatform) || 'shopify');
    setEditVendorName(conn.vendorName ?? '');
    setEditAdminToken('');
    setEditNetoApiUsername('');
    setEditNetoApiKey('');
    setEditFallbackCents(conn.fallbackRateCents);
    setEditFallbackName(conn.fallbackRateName);
    // Close test panel if open for this supplier
    if (testConnId === conn.id) setTestConnId(null);
  }

  async function handleSaveEdit() {
    if (!editingId) return;
    setEditBusy(true);
    try {
      const body: Record<string, unknown> = {
        supplierName: editName.trim(),
        supplierShopifyUrl: editUrl.trim(),
        platform: editPlatform,
        vendorName: editVendorName.trim() || null,
        fallbackRateCents: editFallbackCents,
        fallbackRateName: editFallbackName.trim(),
      };
      if (!['neto', 'neto_storefront'].includes(editPlatform) && editAdminToken.trim()) {
        body.supplierAdminToken = editAdminToken.trim();
      }
      if (editPlatform === 'neto') {
        if (editNetoApiUsername.trim()) body.netoApiUsername = editNetoApiUsername.trim();
        if (editNetoApiKey.trim()) body.netoApiKey = editNetoApiKey.trim();
      }
      const res = await fetch(`${BASE}/api/shipping/suppliers/${editingId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to save');
      toast({ title: 'Supplier updated' });
      setEditingId(null);
      await loadAll();
    } catch (err: any) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setEditBusy(false);
    }
  }

  // Test tool state
  const [testConnId, setTestConnId] = useState<string | null>(null);
  const [testPostcode, setTestPostcode] = useState('');
  const [testProvince, setTestProvince] = useState('New South Wales');
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<{
    rates: TestRate[];
    testedSkus: { sku: string; quantity: number }[];
    testedTitles: string[];
    matchedBcNames?: string[];
    fallback: { name: string; price_cents: number } | null;
  } | null>(null);
  const [testError, setTestError] = useState('');

  // Vendor product picker state
  const [testProducts, setTestProducts] = useState<VendorProduct[]>([]);
  const [testProductsLoading, setTestProductsLoading] = useState(false);
  const [testProductsAttempted, setTestProductsAttempted] = useState(false);
  const [testProductsError, setTestProductsError] = useState('');
  const [testProductFilter, setTestProductFilter] = useState('');
  const [testSelectedProduct, setTestSelectedProduct] = useState<VendorProduct | null>(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // ── Load data ────────────────────────────────────────────────────────────────

  async function loadAll() {
    setLoading(true);
    try {
      const [storesRes, connRes] = await Promise.all([
        fetch(`${BASE}/api/stores`, { headers }),
        fetch(`${BASE}/api/shipping/suppliers`, { headers }),
      ]);
      // /api/stores returns a plain array of store objects
      const storesData: ShopifyStore[] = await storesRes.json();
      const connData = await connRes.json();
      const storeList = Array.isArray(storesData) ? storesData : [];
      setStores(storeList);
      setConnections(connData.connections ?? []);
      setCarrierServiceByStore(connData.carrierServiceByStore ?? {});
      if (storeList.length > 0 && !newStoreId) {
        setNewStoreId(storeList[0]!.id);
      }
    } catch {
      toast({ title: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  // ── Add supplier ─────────────────────────────────────────────────────────────

  async function handleAdd() {
    if (!newStoreId || !newSupplierName.trim() || !newSupplierUrl.trim()) {
      toast({ title: 'All fields are required', variant: 'destructive' });
      return;
    }
    if (newPlatform === 'neto' && (!newNetoApiUsername.trim() || !newNetoApiKey.trim())) {
      toast({ title: 'Enter the Neto API username and secure key', variant: 'destructive' });
      return;
    }
    if (newPlatform === 'neto_storefront' && !newVendorName.trim()) {
      toast({ title: 'Enter the matching Shopify vendor name for the Neto storefront supplier', variant: 'destructive' });
      return;
    }
    setAddBusy(true);
    try {
      const res = await fetch(`${BASE}/api/shipping/suppliers`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: newStoreId,
          supplierName: newSupplierName.trim(),
          supplierShopifyUrl: newSupplierUrl.trim(),
          platform: newPlatform,
          vendorName: newVendorName.trim() || null,
          ...(newPlatform === 'neto' ? {
            netoApiUsername: newNetoApiUsername.trim(),
            netoApiKey: newNetoApiKey.trim(),
          } : {}),
          fallbackRateCents: newFallbackCents,
          fallbackRateName: newFallbackName.trim() || 'Standard Shipping',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to add supplier');
      toast({ title: `${newSupplierName} added` });
      setShowAddForm(false);
      setNewSupplierName('');
      setNewSupplierUrl('');
      setNewPlatform('shopify');
      setNewVendorName('');
      setNewNetoApiUsername('');
      setNewNetoApiKey('');
      setNewFallbackCents(1500);
      setNewFallbackName('Standard Shipping');
      await loadAll();
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setAddBusy(false);
    }
  }

  // ── Enable / disable ─────────────────────────────────────────────────────────

  async function handleToggle(conn: SupplierConnection) {
    const endpoint = conn.enabled ? 'disable' : 'enable';
    setActionBusy((s) => ({ ...s, [conn.id]: true }));
    try {
      const res = await fetch(`${BASE}/api/shipping/suppliers/${conn.id}/${endpoint}`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? `Failed to ${endpoint}`);
      toast({
        title: conn.enabled
          ? `${conn.supplierName} disabled — carrier service unregistered`
          : `${conn.supplierName} enabled — carrier service registered`,
      });
      await loadAll();
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setActionBusy((s) => ({ ...s, [conn.id]: false }));
    }
  }

  // ── Delete ───────────────────────────────────────────────────────────────────

  async function handleDelete(conn: SupplierConnection) {
    if (!confirm(`Remove ${conn.supplierName}? This will unregister the carrier service.`)) return;
    setActionBusy((s) => ({ ...s, [conn.id]: true }));
    try {
      const res = await fetch(`${BASE}/api/shipping/suppliers/${conn.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? 'Delete failed');
      }
      toast({ title: `${conn.supplierName} removed` });
      await loadAll();
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setActionBusy((s) => ({ ...s, [conn.id]: false }));
    }
  }

  // ── Refresh cache ─────────────────────────────────────────────────────────────

  async function handleRefreshCache(conn: SupplierConnection) {
    setActionBusy((s) => ({ ...s, [`cache_${conn.id}`]: true }));
    try {
      const res = await fetch(`${BASE}/api/shipping/suppliers/${conn.id}/refresh-cache`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Refresh failed');
      toast({ title: `Cache refreshed — ${data.skuCount} SKUs loaded` });
      await loadAll();
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setActionBusy((s) => ({ ...s, [`cache_${conn.id}`]: false }));
    }
  }

  // ── Test rates ────────────────────────────────────────────────────────────────

  function openTestPanel(conn: SupplierConnection) {
    const isOpen = testConnId === conn.id;
    setTestConnId(isOpen ? null : conn.id);
    setTestResult(null);
    setTestError('');
    setTestProducts([]);
    setTestProductsAttempted(false);
    setTestProductsError('');
    setTestProductFilter('');
    setTestSelectedProduct(null);
    setTestPostcode('');
  }

  async function loadVendorProducts(conn: SupplierConnection) {
    setTestProductsLoading(true);
    setTestProductsAttempted(true);
    setTestProductsError('');
    setTestProducts([]);
    setTestSelectedProduct(null);
    try {
      const res = await fetch(
        `${BASE}/api/shipping/vendor-products?supplierId=${conn.id}&storeId=${conn.storeId}`,
        { headers, cache: 'no-store' },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to load products');
      setTestProducts(data.products ?? []);
    } catch (e: any) {
      setTestProductsError(e.message);
    } finally {
      setTestProductsLoading(false);
    }
  }

  async function handleTest(conn: SupplierConnection) {
    if (!testPostcode.trim()) {
      toast({ title: 'Enter a postcode first', variant: 'destructive' });
      return;
    }
    setTestBusy(true);
    setTestResult(null);
    setTestError('');
    try {
      const selectedSku = testSelectedProduct?.variants.find(v => v.sku)?.sku;
      if (conn.platform === 'neto_storefront' && !selectedSku) {
        throw new Error('Load products and select a product with a SKU before fetching Neto public storefront rates.');
      }
      const body: Record<string, unknown> = {
        supplierId: conn.id,
        postcode: testPostcode.trim(),
        province: testProvince,
      };
      // BC + vendorName: test by product title (BC does its own catalogue search).
      // Shopify (any): always use SKU-based matching, whether a product is selected or not.
      if (testSelectedProduct && conn.platform === 'bigcommerce' && conn.vendorName) {
        body.titles = [testSelectedProduct.title];
      } else if (testSelectedProduct) {
        // Shopify: use first SKU from the selected product's variants
        const sku = testSelectedProduct.variants.find(v => v.sku)?.sku;
        if (sku) body.skus = [{ sku, quantity: 1 }];
      }
      const res = await fetch(`${BASE}/api/shipping/test-rates`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Test failed');
      setTestResult(data);
    } catch (e: any) {
      setTestError(e.message);
    } finally {
      setTestBusy(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  const storeMap = new Map(stores.map((s) => [s.id, s]));

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Truck className="w-6 h-6 text-primary" />
            Live Supplier Shipping Rates
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Fetch real-time shipping quotes directly from your supplier's checkout at the moment
            a customer asks — so your rates always match what you'll be charged.
          </p>
        </div>

        {/* How it works */}
        <div className="bg-muted/30 border border-border rounded-xl p-5 space-y-3">
          <h2 className="text-sm font-medium text-foreground">How it works</h2>
          <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
            <li>Add your drop-ship supplier's Shopify store URL below.</li>
            <li>Enable live rates — this registers a Carrier Service with your Shopify store.</li>
            <li>
              When a customer enters their address at checkout, Shopify calls SoraFlo, which
              fetches the exact rates your supplier would charge for those items to that address.
            </li>
            <li>Your customer sees real rates. You're never over- or under-charging.</li>
          </ol>
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-400 mt-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              Carrier-calculated shipping requires{' '}
              <strong>Shopify Advanced plan or higher</strong>, or annual billing.
              You'll see an error on Enable if your store doesn't qualify.
            </span>
          </div>
        </div>

        {/* No stores warning */}
        {stores.length === 0 && (
          <div className="bg-muted/30 border border-border rounded-xl p-6 text-center text-muted-foreground text-sm">
            No stores connected yet.{' '}
            <a href={`${BASE}/stores`} className="text-primary hover:underline">
              Connect a store
            </a>{' '}
            first.
          </div>
        )}

        {/* Supplier connections list */}
        {connections.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Configured Suppliers</h2>
            {connections.map((conn) => {
              const store = storeMap.get(conn.storeId);
              const busy = actionBusy[conn.id];
              const cacheBusy = actionBusy[`cache_${conn.id}`];
              const isTestOpen = testConnId === conn.id;

              return (
                <div
                  key={conn.id}
                  className="bg-muted/30 border border-border rounded-xl overflow-hidden"
                >
                  {/* Main row */}
                  <div className="flex items-center gap-3 px-4 py-3">
                    {/* Status indicator */}
                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                      conn.enabled ? 'bg-emerald-400' : 'bg-muted'
                    }`} />

                    {/* Name + store */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-foreground truncate">
                        {conn.supplierName}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        <span className="truncate">{conn.supplierShopifyUrl}</span>
                        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          conn.platform === 'bigcommerce'
                            ? 'bg-blue-500/15 text-blue-400'
                            : conn.platform === 'wordpress'
                            ? 'bg-violet-500/15 text-violet-400'
                            : conn.platform === 'neto' || conn.platform === 'neto_storefront'
                            ? 'bg-amber-500/15 text-amber-400'
                            : 'bg-emerald-500/15 text-emerald-400'
                        }`}>
                          {conn.platform === 'bigcommerce' ? 'BC' : conn.platform === 'wordpress' ? 'WC' : conn.platform === 'neto' ? 'Neto API' : conn.platform === 'neto_storefront' ? 'Neto web' : 'Shopify'}
                        </span>
                        {store && (
                          <>
                            <span>·</span>
                            <span className="shrink-0">{store.nickname}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Cache pill */}
                    <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 border border-border/60 rounded px-2 py-1 shrink-0">
                      <Package className="w-3 h-3" />
                      <span>{conn.platform === 'neto_storefront' ? 'Public cart' : `${conn.cacheInfo.skuCount} SKUs`}</span>
                      <span>·</span>
                      <span>{timeAgo(conn.cacheInfo.builtAt)}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      {conn.platform !== 'neto_storefront' && (
                        <button
                          onClick={() => handleRefreshCache(conn)}
                          disabled={!!cacheBusy}
                          title="Refresh SKU cache"
                          className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                        >
                          {cacheBusy
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <RefreshCw className="w-3.5 h-3.5" />}
                        </button>
                      )}

                      <button
                        onClick={() => openTestPanel(conn)}
                        title="Test rates"
                        className={`p-1.5 rounded transition-colors ${
                          isTestOpen
                            ? 'bg-primary/20 text-primary'
                            : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Search className="w-3.5 h-3.5" />
                      </button>

                      <button
                        onClick={() => handleToggle(conn)}
                        disabled={!!busy}
                        title={conn.enabled ? 'Disable' : 'Enable'}
                        className="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-40"
                      >
                        {busy
                          ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                          : conn.enabled
                          ? <ToggleRight className="w-5 h-5 text-emerald-400" />
                          : <ToggleLeft className="w-5 h-5 text-muted-foreground" />}
                      </button>

                      <button
                        onClick={() => editingId === conn.id ? setEditingId(null) : openEdit(conn)}
                        title="Edit"
                        className={`p-1.5 rounded transition-colors ${
                          editingId === conn.id
                            ? 'bg-primary/20 text-primary'
                            : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>

                      <button
                        onClick={() => handleDelete(conn)}
                        disabled={!!busy}
                        title="Remove"
                        className="p-1.5 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors disabled:opacity-40"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Status strip — carrier service is per-store, not per-supplier */}
                  {(() => {
                    const storeCarrier = carrierServiceByStore[conn.storeId];
                    const storeHasCarrierService = !!storeCarrier?.carrierServiceId;
                    return (
                      <div className={`px-4 py-1.5 text-xs flex items-center gap-1.5 border-t border-border/50 ${
                        conn.enabled
                          ? 'bg-emerald-500/5 text-emerald-400'
                          : 'bg-muted/20 text-muted-foreground'
                      }`}>
                        {conn.enabled
                          ? <><CheckCircle2 className="w-3 h-3" /> Active — included in live rate calculation</>
                          : <><XCircle className="w-3 h-3" /> Inactive — toggle to include in live rates</>}
                        {storeHasCarrierService && (
                          <span className="ml-auto opacity-60 shrink-0">
                            Store carrier service active
                          </span>
                        )}
                      </div>
                    );
                  })()}

                  {/* Test rates panel */}
                  {isTestOpen && (
                    <div className="border-t border-border/50 bg-muted/20 px-4 py-4 space-y-4">
                      <div className="text-xs font-medium text-foreground flex items-center gap-1.5">
                        <Zap className="w-3.5 h-3.5 text-primary" />
                        Test Live Rates
                      </div>

                      {/* Product picker — shown when supplier has a vendorName */}
                      {conn.vendorName && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                              Select a product to test{' '}
                              <span className="text-foreground/60">({conn.vendorName})</span>
                            </span>
                          {testProducts.length === 0 && !testProductsLoading && (
                              <button
                                onClick={() => loadVendorProducts(conn)}
                                className="text-xs text-primary hover:underline"
                              >
                                Load products
                              </button>
                            )}
                            {testProducts.length > 0 && (
                              <button
                                onClick={() => { setTestProducts([]); setTestSelectedProduct(null); setTestProductFilter(''); }}
                                className="text-xs text-muted-foreground hover:text-foreground"
                              >
                                Clear
                              </button>
                            )}
                          </div>

                          {testProductsLoading && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              Loading products…
                            </div>
                          )}

                          {testProductsError && (
                            <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-400">
                              {testProductsError}
                            </div>
                          )}

                          {testProducts.length > 0 && (
                            <div className="space-y-1.5">
                              <input
                                type="text"
                                placeholder="Filter products…"
                                value={testProductFilter}
                                onChange={(e) => setTestProductFilter(e.target.value)}
                                className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                              />
                              <div className="max-h-48 overflow-y-auto space-y-0.5 rounded-lg border border-border/60 bg-muted/20">
                                {testProducts
                                  .filter(p => !testProductFilter || p.title.toLowerCase().includes(testProductFilter.toLowerCase()))
                                  .map(product => (
                                    <button
                                      key={product.id}
                                      onClick={() => setTestSelectedProduct(
                                        testSelectedProduct?.id === product.id ? null : product
                                      )}
                                      className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                                        testSelectedProduct?.id === product.id
                                          ? 'bg-primary/15 text-primary'
                                          : 'text-foreground hover:bg-muted/50'
                                      }`}
                                    >
                                      {product.title}
                                    </button>
                                  ))
                                }
                                {testProducts.filter(p => !testProductFilter || p.title.toLowerCase().includes(testProductFilter.toLowerCase())).length === 0 && (
                                  <div className="px-3 py-3 text-xs text-muted-foreground text-center">
                                    No products match "{testProductFilter}"
                                  </div>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {testProducts.length} product{testProducts.length !== 1 ? 's' : ''} loaded
                                {testSelectedProduct && (
                                  <> · <span className="text-primary">"{testSelectedProduct.title}" selected</span></>
                                )}
                              </div>
                            </div>
                          )}

                          {testProducts.length === 0 && !testProductsLoading && !testProductsError && !testProductsAttempted && (
                            <p className="text-xs text-muted-foreground">
                              {conn.platform === 'neto_storefront'
                                ? 'Load products and select one with a SKU before fetching public storefront rates.'
                                : 'Load products to pick a specific one, or leave empty to test with the first SKU in the catalog.'}
                            </p>
                          )}
                          {testProducts.length === 0 && !testProductsLoading && !testProductsError && testProductsAttempted && (
                            <p className="text-xs text-amber-400">
                              No Shopify products were found for vendor “{conn.vendorName}”. Check that the vendor name matches the Shopify Vendor field exactly, then try again.
                            </p>
                          )}
                        </div>
                      )}

                      {/* Address inputs + run button */}
                      <div className="flex gap-2 flex-wrap">
                        <input
                          type="text"
                          placeholder="Postcode (e.g. 3000)"
                          value={testPostcode}
                          onChange={(e) => setTestPostcode(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleTest(conn)}
                          className="w-32 bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                        />
                        <select
                          value={testProvince}
                          onChange={(e) => setTestProvince(e.target.value)}
                          className="bg-muted/50 border border-border rounded-lg px-2 py-1.5 text-sm text-foreground outline-none focus:border-primary/50 transition-colors"
                        >
                          {[
                            ['New South Wales','NSW'],
                            ['Victoria','VIC'],
                            ['Queensland','QLD'],
                            ['South Australia','SA'],
                            ['Western Australia','WA'],
                            ['Tasmania','TAS'],
                            ['Australian Capital Territory','ACT'],
                            ['Northern Territory','NT'],
                          ].map(([name, code]) => (
                            <option key={code} value={name}>{code}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleTest(conn)}
                          disabled={testBusy}
                          className="px-4 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                        >
                          {testBusy
                            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Testing…</>
                            : 'Fetch Rates'}
                        </button>
                      </div>
                      {!conn.vendorName && conn.platform !== 'neto_storefront' && (
                        <p className="text-xs text-muted-foreground">
                          Uses the first SKU in the supplier catalog as a test item.
                        </p>
                      )}

                      {testError && (
                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-400">
                          {testError}
                        </div>
                      )}

                      {testResult && (
                        <div className="space-y-2">
                          {/* What was tested */}
                          <div className="space-y-0.5">
                            <div className="text-xs text-muted-foreground">
                              {testResult.testedTitles?.length > 0
                                ? <>Tested: <span className="text-foreground">{testResult.testedTitles.join(', ')}</span></>
                                : testResult.testedSkus?.length > 0
                                ? <>Tested SKU{testResult.testedSkus.length > 1 ? 's' : ''}: {testResult.testedSkus.map((s: any) => `${s.sku} ×${s.quantity}`).join(', ')}</>
                                : null
                              }
                            </div>
                            {testResult.matchedBcNames && testResult.matchedBcNames.length > 0 && (
                              <div className="text-xs text-muted-foreground">
                                BC product matched:{' '}
                                <span className="text-emerald-400">{testResult.matchedBcNames.join(', ')}</span>
                              </div>
                            )}
                            {testResult.testedTitles?.length > 0 && (!testResult.matchedBcNames || testResult.matchedBcNames.length === 0) && (
                              <div className="text-xs text-amber-400">
                                ⚠ No BC product matched — used placeholder for zone lookup
                              </div>
                            )}
                          </div>
                          {testResult.rates.length === 0 && testResult.fallback ? (
                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-400 flex items-center gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                              No live rates returned — fallback would apply:{' '}
                              {testResult.fallback.name} ({formatCents(testResult.fallback.price_cents)})
                            </div>
                          ) : (
                            <div className="space-y-1">
                              {testResult.rates.map((r, i) => (
                                <div
                                  key={i}
                                  className="flex items-center justify-between bg-muted/50 border border-border/60 rounded-lg px-3 py-2"
                                >
                                  <div>
                                    <div className="text-sm font-medium text-foreground">{r.service_name}</div>
                                    {r.description && (
                                      <div className="text-xs text-muted-foreground">{r.description}</div>
                                    )}
                                  </div>
                                  <div className="text-sm font-semibold text-foreground">
                                    {formatCents(r.total_price)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Edit panel */}
                  {editingId === conn.id && (
                    <div className="border-t border-border/50 bg-muted/20 px-4 py-4 space-y-3">
                      <div className="text-xs font-medium text-foreground flex items-center gap-1.5">
                        <Pencil className="w-3.5 h-3.5 text-primary" />
                        Edit Supplier
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="sm:col-span-2">
                          <label className="block text-xs text-muted-foreground mb-1">Platform</label>
                          <div className="flex gap-2">
                            {(['shopify', 'bigcommerce', 'wordpress', 'neto', 'neto_storefront'] as const).map((p) => (
                              <button
                                key={p}
                                type="button"
                                onClick={() => setEditPlatform(p)}
                                className={`flex-1 py-1.5 rounded-lg text-sm border transition-colors ${
                                  editPlatform === p
                                    ? 'bg-primary/20 border-primary/50 text-primary font-medium'
                                    : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground'
                                }`}
                              >
                                {platformLabel(p)}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Display Name</label>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">
                            {platformLabel(editPlatform)} Store URL
                          </label>
                          <input
                            type="text"
                            value={editUrl}
                            onChange={(e) => setEditUrl(e.target.value)}
                              placeholder={editPlatform === 'bigcommerce' ? 'store.com.au' : editPlatform === 'wordpress' ? 'supplier.com.au' : editPlatform === 'neto' || editPlatform === 'neto_storefront' ? 'performancesuspension.com.au' : 'store.myshopify.com'}
                            className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs text-muted-foreground mb-1">
                             Match by Shopify vendor name
                             <span className="ml-1 text-muted-foreground/60">{editPlatform === 'neto_storefront' ? '(required — limits quotes to this supplier’s cart items)' : '(optional — for suppliers whose products have no SKUs)'}</span>
                          </label>
                          <input
                            type="text"
                            value={editVendorName}
                            onChange={(e) => setEditVendorName(e.target.value)}
                            placeholder="e.g. Offroad Animal"
                            className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Must match the Vendor field on the Shopify products exactly.</p>
                        </div>
                         {editPlatform === 'neto' ? (
                           <div className="sm:col-span-2 space-y-2 bg-amber-500/8 border border-amber-500/20 rounded-lg px-3 py-3">
                             <div className="text-xs text-amber-300">
                               Neto / Maropost uses its official API for product SKUs and shipping quotes. Credentials are encrypted before they are saved.
                             </div>
                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                               <div>
                                 <label className="block text-xs text-muted-foreground mb-1">Neto API Username</label>
                                 <input
                                   type="text"
                                   value={editNetoApiUsername}
                                   onChange={(e) => setEditNetoApiUsername(e.target.value)}
                                   placeholder={conn.netoApiUsernameMasked || 'Enter API username'}
                                   autoComplete="off"
                                   className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                                 />
                               </div>
                               <div>
                                 <label className="block text-xs text-muted-foreground mb-1">Neto API Secure Key</label>
                                 <input
                                   type="password"
                                   value={editNetoApiKey}
                                   onChange={(e) => setEditNetoApiKey(e.target.value)}
                                   placeholder={conn.netoApiKeyConfigured ? 'Configured — leave blank to keep' : 'Enter secure key'}
                                   autoComplete="new-password"
                                   className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors font-mono"
                                 />
                               </div>
                             </div>
                             <p className="text-xs text-muted-foreground">Generate these values in Neto Control Panel → Settings → API.</p>
                           </div>
                          ) : editPlatform === 'neto_storefront' ? (
                            <div className="sm:col-span-2 bg-amber-500/8 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-300">
                              Uses the public Neto cart and shipping calculator — no API credentials are needed. Storefront access can be blocked or change without notice, so the fallback rate remains active whenever a live quote is unavailable.
                            </div>
                          ) : editPlatform !== 'wordpress' ? (
                          <div className="sm:col-span-2">
                            <label className="block text-xs text-muted-foreground mb-1">
                              Admin API Token
                              <span className="ml-1 text-muted-foreground/60">(optional — bypasses Cloudflare / rate limits when building the SKU catalogue)</span>
                            </label>
                            <input
                              type="password"
                              value={editAdminToken}
                              onChange={(e) => setEditAdminToken(e.target.value)}
                              placeholder="shpat_…"
                              autoComplete="off"
                              className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors font-mono"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Requires <code className="text-foreground/70">read_products</code> scope for SKU catalogue. Add <code className="text-foreground/70">write_draft_orders</code> to also fetch live carrier rates (Starshipit, Fare Freight, etc.). Stored encrypted.</p>
                          </div>
                        ) : (
                          <div className="sm:col-span-2 bg-violet-500/8 border border-violet-500/20 rounded-lg px-3 py-2 text-xs text-violet-300">
                            WooCommerce uses the public Blocks Store API — no API token needed. SKUs and rates are fetched without credentials.
                          </div>
                        )}
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Fallback Rate Name</label>
                          <input
                            type="text"
                            value={editFallbackName}
                            onChange={(e) => setEditFallbackName(e.target.value)}
                            className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Fallback Rate (cents)</label>
                          <input
                            type="number"
                            min={0}
                            value={editFallbackCents}
                            onChange={(e) => setEditFallbackCents(Number(e.target.value))}
                            className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                          />
                          <p className="text-xs text-muted-foreground mt-1">= {formatCents(editFallbackCents)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={handleSaveEdit}
                          disabled={editBusy || !editName.trim() || !editUrl.trim()}
                          className="px-4 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                        >
                          {editBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : 'Save Changes'}
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-4 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {connections.length === 0 && stores.length > 0 && (
          <div className="bg-muted/30 border border-border rounded-xl p-8 text-center">
            <Truck className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No suppliers configured yet.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Add your first drop-ship supplier to get started.
            </p>
          </div>
        )}

        {/* Add supplier button / form */}
        {stores.length > 0 && (
          <div>
            {!showAddForm ? (
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add drop-ship supplier
              </button>
            ) : (
              <div className="bg-muted/30 border border-border rounded-xl p-5 space-y-4">
                <h3 className="text-sm font-semibold text-foreground">Add Supplier</h3>

                <div className="space-y-3">
                  {/* Store picker */}
                  <ShopifyTargetStoreSelector
                    stores={stores}
                    value={newStoreId}
                    onChange={setNewStoreId}
                    label="Your Shopify Store"
                  />

                  {/* Supplier name */}
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Supplier Display Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Canna Fabrication"
                      value={newSupplierName}
                      onChange={(e) => setNewSupplierName(e.target.value)}
                      className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                    />
                  </div>

                  {/* Platform selector */}
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Platform</label>
                    <div className="flex gap-2">
                       {(['shopify', 'bigcommerce', 'wordpress', 'neto', 'neto_storefront'] as const).map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setNewPlatform(p)}
                          className={`flex-1 py-1.5 rounded-lg text-sm border transition-colors ${
                            newPlatform === p
                              ? 'bg-primary/20 border-primary/50 text-primary font-medium'
                              : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground'
                          }`}
                        >
                           {platformLabel(p)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Vendor name match */}
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">
                      Match by Shopify vendor name
                      <span className="ml-1 text-muted-foreground/60">(optional)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Offroad Animal"
                      value={newVendorName}
                      onChange={(e) => setNewVendorName(e.target.value)}
                      className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      When set, rates fire for any cart item whose Shopify vendor matches this name — even if the product has no SKU.
                    </p>
                  </div>

                  {/* Supplier URL */}
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">
                       Supplier's {platformLabel(newPlatform)} Store URL
                    </label>
                    <input
                      type="text"
                       placeholder={newPlatform === 'bigcommerce' ? 'offroadanimal.com.au' : newPlatform === 'wordpress' ? 'npcperformance.com.au' : newPlatform === 'neto' || newPlatform === 'neto_storefront' ? 'performancesuspension.com.au' : 'cannafab.com.au'}
                      value={newSupplierUrl}
                      onChange={(e) => setNewSupplierUrl(e.target.value)}
                      className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      No https:// needed — just the domain name.
                    </p>
                  </div>

                  {newPlatform === 'neto' && (
                    <div className="space-y-2 bg-amber-500/8 border border-amber-500/20 rounded-lg px-3 py-3">
                      <div className="text-xs text-amber-300">
                        Neto / Maropost uses its official API for product SKUs and shipping quotes. Credentials are encrypted before they are saved.
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Neto API Username</label>
                          <input
                            type="text"
                            value={newNetoApiUsername}
                            onChange={(e) => setNewNetoApiUsername(e.target.value)}
                            placeholder="Enter API username"
                            autoComplete="off"
                            className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Neto API Secure Key</label>
                          <input
                            type="password"
                            value={newNetoApiKey}
                            onChange={(e) => setNewNetoApiKey(e.target.value)}
                            placeholder="Enter secure key"
                            autoComplete="new-password"
                            className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors font-mono"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">Generate these values in Neto Control Panel → Settings → API.</p>
                    </div>
                  )}
                  {newPlatform === 'neto_storefront' && (
                    <div className="space-y-1 bg-amber-500/8 border border-amber-500/20 rounded-lg px-3 py-3 text-xs text-amber-300">
                      <p>Uses the supplier’s public Neto cart and shipping calculator. No API username or secure key is required.</p>
                      <p className="text-muted-foreground">The vendor name above is required so only this supplier’s items are added to the temporary cart. If the supplier blocks automated storefront requests or changes its checkout, your fallback rate is used.</p>
                    </div>
                  )}

                  {/* Fallback rate */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Fallback Rate Name</label>
                      <input
                        type="text"
                        placeholder="Standard Shipping"
                        value={newFallbackName}
                        onChange={(e) => setNewFallbackName(e.target.value)}
                        className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Fallback Rate (AUD)</label>
                      <input
                        type="number"
                        placeholder="15.00"
                        step="0.50"
                        min="0"
                        value={(newFallbackCents / 100).toFixed(2)}
                        onChange={(e) => setNewFallbackCents(Math.round(parseFloat(e.target.value || '0') * 100))}
                        className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/50 transition-colors"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-1">
                    Shown at checkout if live rate fetch fails (so checkout is never broken).
                  </p>
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleAdd}
                    disabled={addBusy}
                    className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {addBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</> : 'Add Supplier'}
                  </button>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
